import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { baseUrl, Endpoints } from '../../api';
import { AppThunk } from '../store';
import { smartRequest } from '../../api/index';

// define the shape of a product
// replace fields with your actual backend schema
export interface Product {
    id: number;
    name: string;
    // add more fields here
}

// shape of API response
export interface ProductListResponse {
    items: Product[];
    totalCount?: number;
}

// redux state for this slice
export interface ProductListState {
    /** Page currently shown (0 while a fresh page is loading) */
    pageNumber: number;
    items: Product[];
    status: 'idle' | 'loading' | 'failed';
    hasMore: boolean;
    /** Total matching ads when the API reports it; `null` while it only returns a bare page of items */
    totalCount: number | null;
    currentSearch: string;
    currentCategory: string;
    isSearch: boolean;
    /** Bumped on every fresh (non-load-more) fetch; stale in-flight requests from a prior generation are ignored on arrival */
    requestGeneration: number;
}

/** Ads per page. Fixed by the API: GetItems takes a page number, not a page size. */
export const PRODUCT_LIST_PAGE_SIZE = 60;

// default redux state
const initialState: ProductListState = {
    items: [],
    status: 'idle',
    hasMore: true,
    totalCount: null,
    currentSearch: '',
    currentCategory: '',
    isSearch: false,
    pageNumber: 0,
    requestGeneration: 0
};

// server expects this shape for queries
export interface ProductListParams {
    pageNumber: number;
    sortby: { columName: string; type: 'asc' | 'desc' };
    filter: {
        categoryId: number;
        parentCategoryId: number;
        countryId: number;
        regionId: number;
        cityId: number;
        fromPrice: number;
        toPrice: number;
        categories: number[];
    };
    lang: string;
    search: string;
    userId: number;
    showCurrency: string;
}

const initialParams: ProductListParams = {
    pageNumber: 1,
    sortby: { columName: 'createdDate', type: 'desc' },
    filter: {
        categoryId: 0,
        parentCategoryId: 0,
        countryId: 0,
        regionId: 0,
        cityId: 0,
        fromPrice: -1,
        toPrice: -1,
        categories: []
    },
    lang: 'en',
    search: '',
    userId: 0,
    showCurrency: ''
};

/** `generation` pins a request to the productList state it was issued against — a stale response whose
 * generation no longer matches current state (because a fresh search/filter/sort reset happened meanwhile)
 * is ignored in the reducers below instead of being merged into unrelated results. */
interface FetchProductListArg {
    params: ProductListParams;
    generation: number;
}

type ProductListPage = { items: Product[]; totalCount: number | null };

/** GetItems currently returns a bare array; also accept `{ items | data, totalCount | total | count }` so a
 * backend that starts reporting the total gets full page numbering without another frontend change. */
export function readProductListPage(response: unknown): ProductListPage {
    if (Array.isArray(response)) {
        return { items: response as Product[], totalCount: null };
    }
    if (response && typeof response === 'object') {
        const body = response as Record<string, unknown>;
        const items = Array.isArray(body.items) ? body.items : Array.isArray(body.data) ? body.data : [];
        const total = [body.totalCount, body.total, body.count].find((value) => typeof value === 'number' && Number.isFinite(value));
        return { items: items as Product[], totalCount: typeof total === 'number' ? total : null };
    }
    return { items: [], totalCount: null };
}

// No `condition` gate here: `requestGeneration` (bumped by `resetPage`, checked in the reducers below)
// is what makes duplicate/superseded requests safe — a response for a page or query the user has
// already left is ignored instead of replacing what is on screen.
export const fetchProductList = createAsyncThunk<ProductListPage, FetchProductListArg>(
    'productList/fetch',
    async ({ params }) => {
        const response = await smartRequest(baseUrl + Endpoints.productList, {
            method: 'POST',
            body: JSON.stringify(params)
        });

        return readProductListPage(response);
    }
);

/** Loads one page of the feed (`data.pageNumber`, default 1), replacing whatever page was shown before. */
export const fetchItems =
    (data: Partial<ProductListParams>): AppThunk =>
    async (dispatch, getState) => {
        const { params } = getState();
        const page = Math.max(1, Math.floor(Number(data.pageNumber) || 1));

        const merged: ProductListParams = {
            ...initialParams,
            ...params,
            ...data,
            pageNumber: page,
            sortby: {
                ...initialParams.sortby,
                ...(params?.sortby ?? {}),
                ...(data?.sortby ?? {}),
                type: ((data?.sortby?.type ?? params?.sortby?.type) || 'desc') as 'asc' | 'desc'
            }
        };

        dispatch(resetPage());
        // Read the post-reset generation — resetPage() just bumped it synchronously above
        dispatch(fetchProductList({ params: merged, generation: getState().productList.requestGeneration }));
    };

export const productListSlice = createSlice({
    name: 'productList',
    initialState,
    reducers: {
        setItems: (state, action: PayloadAction<Product[]>) => {
            state.items = action.payload;
        },
        setCurrentSearch: (state, action: PayloadAction<string>) => {
            state.currentSearch = action.payload;
            state.isSearch = true;
        },
        setCurrentCategory: (state, action: PayloadAction<string>) => {
            state.currentCategory = action.payload;
            state.isSearch = false;
        },
        resetPage: (state) => {
            state.pageNumber = 0;
            state.items = [];
            state.hasMore = true;
            state.requestGeneration += 1;
        },
        resetSearch: (state) => {
            state.currentSearch = '';
            state.isSearch = false;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchProductList.pending, (state, action) => {
                if (action.meta.arg.generation !== state.requestGeneration) return;
                state.status = 'loading';
            })
            .addCase(fetchProductList.fulfilled, (state, action) => {
                if (action.meta.arg.generation !== state.requestGeneration) return;
                const { items, totalCount } = action.payload;
                const page = action.meta.arg.params.pageNumber;

                state.items = items;
                state.totalCount = totalCount;
                state.pageNumber = page;
                state.hasMore = totalCount !== null ? page * PRODUCT_LIST_PAGE_SIZE < totalCount : items.length >= PRODUCT_LIST_PAGE_SIZE;
                state.status = 'idle';
            })
            .addCase(fetchProductList.rejected, (state, action) => {
                if (action.meta.arg.generation !== state.requestGeneration) return;
                state.status = 'failed';
            });
    }
});

export const { setItems, setCurrentSearch, setCurrentCategory, resetPage, resetSearch } = productListSlice.actions;

export {
    selectProductListItems as selectItems,
    selectProductListStatus as selectStatus,
    selectProductListHasMore,
    selectProductListTotalCount,
    selectProductListPageNumber,
    selectCurrentSearch,
    selectCurrentCategory,
    selectIsSearch as isSearch
} from '../selectors';

export default productListSlice.reducer;

// thunk wrapper to merge params with current redux params
